import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Table } from './styles';
import { useAuth } from '../../hooks/auth';
import { useParams } from 'react-router-dom';
import Api, { exceptionNotificationAPI } from '../../services/api';
import Select, { components as selectComponents } from 'react-select';
import TableLoading from '../../components/TableLoading/index';
import { error_message } from '../../components/Toast/index.jsx';
import { FaArrowRight, FaAngleDown, FaAngleUp } from 'react-icons/fa';
import GetTypeInput from '../../components/FormInput/set';
import { NumericFormat } from 'react-number-format';
import ModalLoading from '../../components/ModalLoading/index';
import ChatWindow, { getCanManageAllDiscussionTags } from '../../components/Chat/index';
import ChatButton from '../../components/ChatButton/index';
import { ChatWrapper } from './styles';
import moment from 'moment';
import Swal from 'sweetalert2';
import { formSelectStyles, formSelectControlFullWidth } from '../../components/FormInput/formSelectStyles';

const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== 'string') return `rgba(128, 128, 128, ${alpha})`;
    let h = hex.replace('#', '').trim();
    if (h.length === 3) {
        h = h.split('').map((c) => c + c).join('');
    }
    if (h.length !== 6) return `rgba(128, 128, 128, ${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if (Number.isNaN(r + g + b)) return `rgba(128, 128, 128, ${alpha})`;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const tagPillBaseColor = (color) => color || '#9ca3af';

const TagFilterMultiValueLabel = (props) => {
    const c = tagPillBaseColor(props.data?.color);
    return (
        <selectComponents.MultiValueLabel {...props}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span
                    aria-hidden
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: c,
                        flexShrink: 0,
                    }}
                />
                <span style={{ color: '#1f2937' }}>{props.data.label}</span>
            </span>
        </selectComponents.MultiValueLabel>
    );
};

const discussionsTagFilterSelectComponents = {
    MultiValueLabel: TagFilterMultiValueLabel,
};

const DiscussionDashboard = () => {
    const { user, userRoles } = useAuth();
    const { companyID, reportYearID, operationID, moduleID } = useParams();

    const [userObject, setUserObject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [discussionStatus, setDiscussionStatus] = useState("");

    //Company Select Variables
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(companyID);

    //Operation Select Variables
    const [operations, setOperations] = useState([]);
    const [selectedOperation, setSelectedOperation] = useState(operationID);

    //Module Select Variables
    const [modules, setModules] = useState([]);
    const [modulesFiltered, setModulesFiltered] = useState([]);
    const [selectedModule, setSelectedModule] = useState(moduleID);

    //Tag Select Variables
    const [tags, setTags] = useState([]);
    const [selectedTag, setSelectedTag] = useState([]);

    //Crop Year Select Variables
    const [cropYears, setCropYears] = useState([]);
    const [selectedReportYear, setSelectedReportYear] = useState(reportYearID);

    //Ref Number Select Variables
    const [refNumberSelect, setRefNumberSelect] = useState([]);
    const [selectedRefNumber, setSelectedRefNumber] = useState(null);

    //Load Table Versions
    const [loadingView, setLoadingView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [viewSpecificData, setViewSpecificData] = useState(null);
    const [selectedCompanyIsSourcingGroup, setSelectedCompanyIsSourcingGroup] = useState(false);

    //Culture Types Select Variables
    const [cultureTypes, setCultureTypes] = useState([]);
    const [selectedCultureType, setSelectedCultureType] = useState(null);

    const [isFirstLoading, setIsFirstLoading] = useState(!(companyID && reportYearID && operationID && moduleID));

    const pendingOpLoadOptsRef = useRef({});

    //Chat
    const defaultChatRequest = {
        "reportYearID": null,
        "companyId": null,
        "moduleId": null,
        "subSectionKPIID": null,
        "kpiId": null,
        "cultureTypeID": null,
        "fuelTypeId": null,
        "curingBarnID": null,
        "countryId": null,
        "row": null,
        "column": null
    };
    const [showChat, setShowChat] = useState(false);
    const [chatRequest, setChatRequest] = useState({ ...defaultChatRequest });
    const [kpiDiscussions, setKpiDiscussions] = useState([]);

    useEffect(() => {
        var userAux = typeof (user) == "string" ? JSON.parse(user) : user;
        setUserObject(userAux);

        if (!userAux.companyID || userAux.groupID) {
            loadCompanies(userAux);
        } else {
            setSelectedCompany(userAux.companyID);
        }
        loadCropYears(userAux);
        setLoading(false);
    }, []);


    useEffect(() => {
        setChatRequest({
            ...defaultChatRequest,
            "reportYearID": selectedReportYear,
            "companyId": selectedCompany,
            "moduleId": selectedModule
        });
    }, [selectedCompany, selectedReportYear, selectedModule])

    useEffect(() => {
        if (selectedCompany && selectedReportYear) {
            Api.get(`CultureType/GetSelectedCultureTypes?companyID=${selectedCompany}&reportYearID=${selectedReportYear}`)
                .then((result) => {
                    if (result && result.data) {
                        setCultureTypes([...result.data.response]);
                    }
                }).catch((error) => {
                    exceptionNotificationAPI(error);
                });
        } else {
            setCultureTypes([]);
            setSelectedCultureType(null);
        }
    }, [selectedCompany, selectedReportYear])

    const loadCompanies = (user) => {
        if (user.groupID != null) {
            Api.get(`Dashboard/getAllCompaniesFilteredByGroup`).then((result) => {
                if (result && result.data) {
                    setCompanies(result.data.response);
                    // setSelectedCompanyIndex([0]);
                }
            }).catch((error) => {
                exceptionNotificationAPI(error);
            });
        }
        else {
            Api.get(`Company/getAllByCulture?cultureID=${user?.selectedCultureID}`).then((result) => {
                if (result && result.data) {
                    setCompanies(result.data.response);

                    if (companyID)
                        setSelectedCompany(companyID);
                    else if (user?.companyID)
                        setSelectedCompany(user.companyID);
                    else
                        setSelectedCompany(result.data.response[0].companyID);
                }
            }).catch((error) => {
                exceptionNotificationAPI(error);
            });
        }
    }

    const loadCropYears = (userAux) => {
        Api.get(`thriveValue/getAllCropsYears?selectedCulture=${userAux.selectedCultureID}`).then((result) => {
            setLoading(true);
            if (result && result.data) {
                setCropYears(result.data.response);

                if (reportYearID) {
                    setSelectedReportYear(reportYearID);
                }
                else if (userAux != null) {
                    setSelectedReportYear(userAux.selectedReportYearID);
                }
            }
            setLoading(false);
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    const loadOperations = (reportYearID, companyIdOverride, opts = {}) => {
        const { forceFirstOperation = false, clearFormAndModule = false } = opts;
        const cid = companyIdOverride !== undefined ? companyIdOverride : selectedCompany;
        if (cid === undefined || cid === null || cid === '') {
            return;
        }
        if (reportYearID === undefined || reportYearID === null || reportYearID === '') {
            return;
        }
        const qs = `reportYearID=${encodeURIComponent(reportYearID)}&companyID=${encodeURIComponent(cid)}`;
        Api.get(`thriveValue/getAllOperationsByReportYear?${qs}`).then((result) => {

            if (result && result.data) {
                let response = result.data.response;
                let firstOp = '';
                if (response.length > 0) {
                    setOperations(response);
                    firstOp = response[0].operationID;
                } else {
                    response.push({
                        operationID: "",
                        description: `No items found, please select another Report Year`,
                    })
                    setOperations(response);
                    firstOp = '';
                }

                if (clearFormAndModule) {
                    setSelectedOperation(null);
                    loadModules(selectedCompanyIsSourcingGroup, reportYearID, cid, null);
                    return;
                }

                let opForModules = firstOp;
                if (forceFirstOperation) {
                    setSelectedOperation(firstOp);
                    opForModules = firstOp;
                } else if (selectedOperation != null && selectedOperation !== '' && response.some(o => o.operationID == selectedOperation)) {
                    opForModules = selectedOperation;
                } else {
                    setSelectedOperation(firstOp);
                    opForModules = firstOp;
                }

                loadModules(selectedCompanyIsSourcingGroup, reportYearID, cid, opForModules);
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    const loadModules = (IsSourcingGroup, reportYearID, companyIdOverride, operationIdForFilter) => {
        if (reportYearID) {
            const cid = companyIdOverride !== undefined ? companyIdOverride : selectedCompany;
            if (cid === undefined || cid === null || cid === '') {
                return;
            }
            const qs = `reportYearID=${encodeURIComponent(reportYearID)}&companyID=${encodeURIComponent(cid)}`;
            let opFilter;
            if (operationIdForFilter === null) {
                opFilter = null;
            } else if (operationIdForFilter !== undefined) {
                opFilter = operationIdForFilter;
            } else {
                opFilter = selectedOperation;
            }
            Api.get(`Dashboard/GetAllByReportYear?${qs}`).then((result) => {
                if (result && result.data) {
                    const all = result.data.response;
                    setModules(all);
                    if (opFilter === null) {
                        setModulesFiltered([]);
                    } else if (opFilter) {

                        if (!IsSourcingGroup) {
                            setModulesFiltered(all.filter(x => x.operationID == opFilter && x.description != "GLT"));
                        }
                        else {
                            setModulesFiltered(all.filter(x => x.operationID == opFilter));
                        }
                    }
                    else {
                        if (!IsSourcingGroup) {
                            setModulesFiltered(all.filter(x => x.description != "GLT"));
                        }
                        else {
                            setModulesFiltered(all);
                        }
                    }
                }
            }).catch((error) => {
                setLoading(false);
                exceptionNotificationAPI(error);
            });
        }

    }

    useEffect(() => {
        if (selectedCompany === undefined || selectedCompany === null || selectedCompany === '') {
            return;
        }
        if (selectedReportYear === undefined || selectedReportYear === null || selectedReportYear === '') {
            return;
        }
        const opts = { ...pendingOpLoadOptsRef.current };
        pendingOpLoadOptsRef.current = {};
        loadOperations(selectedReportYear, selectedCompany, opts);
        // loadOperations depends on selectedOperation for URL/deep-link behaviour; do not add it here to avoid duplicate fetches on operation-only changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany, selectedReportYear]);

    const onChangeCompany = (value) => {
        setSelectedCompany(value);
        setSelectedModule('');
        setSelectedCultureType(null);
        if (selectedReportYear && value !== undefined && value !== null && value !== '') {
            pendingOpLoadOptsRef.current = { clearFormAndModule: true };
        } else {
            setOperations([]);
            setSelectedOperation(null);
            setModules([]);
            setModulesFiltered([]);
        }
    }

    const loadTags = () => {
        Api.get(`Tag/getForDiscussion`)
            .then((result) => {
                const list = result?.data?.response || result?.data || [];
                const formattedTags = list.map((t) => ({
                    value: t.tagID,
                    label: t.tagDescription || t.tagName,
                    color: t.tagColor,
                }));
    
                setTags(formattedTags);
            })
            .catch((error) => {
                console.error("Erro ao carregar tags:", error);
                exceptionNotificationAPI(error);
            });
    };

    // useEffect exclusivo para carregar as opções do Select (Tags)
    useEffect(() => {
        loadTags();
    }, []);
          
    const onChangeReportYearID = (value) => {
        setSelectedReportYear(value);
        setSelectedModule(null);
        pendingOpLoadOptsRef.current = { forceFirstOperation: true };
    }

    const onChangeOperation = (value) => {
        setSelectedModule('');
        var modulesAux = modules.filter(x => x.operationID == value);

        setSelectedOperation(value);
        setModulesFiltered(modulesAux);
    }
    // Estados para controlar a troca de cultura apenas no "Apply"
    const [tempCultureType, setTempCultureType] = useState(null);
    const [appliedCultureType, setAppliedCultureType] = useState(null);

    const loadDiscussionsDashboard = async () => {
        setLoadingView(true);
        const cultureParam = tempCultureType != null ? `&cultureTypeId=${tempCultureType}` : '';
        const tagIdsQuery = selectedTag?.length ? selectedTag.map((id) => `&tagIds=${encodeURIComponent(id)}`).join("") : "";
        var selectedModuleDiscussions = await Api.get(`Discussion/GetKPIDiscussionsByModule?reportYearID=${selectedReportYear}&companyId=${selectedCompany}${selectedOperation && selectedOperation != "undefined" ? `&operationId=${selectedOperation}` : ``}${selectedModule && selectedModule != "undefined" ? `&moduleID=${selectedModule}` : ``}${cultureParam}&filter=${discussionStatus}${tagIdsQuery}`).then((result) => {
            if (result && result.data) {
                setKpiDiscussions(result.data.response);
                setAppliedCultureType(tempCultureType);
                setSelectedCultureType(tempCultureType);

                return result.data.response;
            }
        });

        setChatRequest({
            ...chatRequest,
            "reportYearID": selectedReportYear,
            "companyId": selectedCompany
        });

        if (selectedCompany && selectedReportYear) {
            Api.get(`Discussion/GetDiscussionDashboard?companyId=${selectedCompany}${selectedOperation && selectedOperation != "undefined" ? `&operationId=${selectedOperation}` : ``}${selectedModule && selectedModule != "undefined" ? `&moduleID=${selectedModule}` : ``}&reportYearID=${selectedReportYear}${cultureParam}${tagIdsQuery}`).then((result) => {
                if (result && result.data) {
                    var viewDataAux = result.data.response;
                    var filteredDiscussions = selectedModuleDiscussions;

                    if (discussionStatus) {
                        if (discussionStatus == "NotAnswered") {
                            filteredDiscussions = filteredDiscussions.filter(x => x.lastDiscussionStatus == "Open");
                        }
                        else {
                            filteredDiscussions = filteredDiscussions.filter(x => x.lastDiscussionStatus == discussionStatus);
                        }
                    }
                    var discussionIDs = filteredDiscussions.map(function (discussion) {
                        const id = discussion.discussionID ?? discussion.DiscussionID;
                        return id != null ? Number(id) : null;
                    }).filter((id) => id != null);
                    viewDataAux = viewDataAux.filter((x) => {
                        const id = x.discussionID ?? x.DiscussionID;
                        return discussionIDs.includes(Number(id));
                    });
                    setViewData(viewDataAux);
                }

                setSelectedRefNumber(null);
                setLoadingView(false);
            }).catch((error) => {
                exceptionNotificationAPI(error);
            });
        } else {
            error_message("May you select all filters");
            setLoadingView(false);
        }
    }

    const handleSendDiscussionNotification = async () => {
        await Api.post("MailNotification/sendDiscussionNotificationToCompany", { companyId: selectedCompany, reportYearId: selectedReportYear, operationId: selectedOperation, moduleId: selectedModule, filter: discussionStatus }).then(async (result) => {
            if (result && result.data) {
                if (result.data.response == true) {
                    Swal.fire({
                        title: "",
                        text: "Email sent successfully.",
                        icon: 'success',
                        showDenyButton: false,
                        confirmButtonColor: '#112A61',
                        confirmButtonText: 'Ok',
                        showCloseButton: true
                    });
                }
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    useEffect(() => {
        if (selectedCompany != null && selectedReportYear != null && selectedOperation != null && selectedModule != null) {
            if (!isFirstLoading) {
                loadDiscussionsDashboard();
                setIsFirstLoading(true);
            }
        }
    }, [selectedCompany, selectedReportYear, selectedOperation, selectedModule]);

    const hasCultureTypeFilterModules = modulesFiltered?.some((m) => Number(m.moduleExternalID ?? m.ModuleExternalID) === 15000);
    const discussionChatCultureTypeID =selectedCultureType != null ? selectedCultureType : undefined;

    const cultureTypeLabelById = (id) => {
        if (id == null || id === '' || !cultureTypes?.length) return null;
        const ct = cultureTypes.find((x) => (x.cultureTypeID ?? x.CultureTypeID) == id);
        return ct?.description ?? ct?.Description ?? null;
    };

    /** Quando o dashboard manda nome errado (ex. FCV) mas o id da discussão está certo em GetKPIDiscussionsByModule. */
    const resolveCultureTypeIdByDescription = (description) => {
        if (description == null || String(description).trim() === '' || !cultureTypes?.length) {
            return undefined;
        }
        const target = String(description).trim().toLowerCase();
        const found = cultureTypes.find((ct) => {
            const desc = ct.description ?? ct.Description;
            return String(desc).trim().toLowerCase() === target;
        });
        return found?.cultureTypeID ?? found?.CultureTypeID;
    };
    const rolesList = useMemo(() => {
        if (userRoles == null) return [];
        if (Array.isArray(userRoles)) return userRoles;
        if (typeof userRoles === 'string') {
            try {
                const p = JSON.parse(userRoles);
                return Array.isArray(p) ? p : [];
            } catch {
                return [];
            }
        }
        return [];
    }, [userRoles]);

    const allowedDiscussionTagIdSet = useMemo(() => new Set((tags || []).map((o) => o.value)), [tags]);

    const discussionsTagSelectStyles = useMemo(
        () => ({
            ...formSelectStyles,
            control: (base, state) => formSelectControlFullWidth(base, state),
            multiValue: (base, state) => {
                const c = tagPillBaseColor(state.data?.color);
                return {
                    ...base,
                    borderRadius: 999,
                    backgroundColor: hexToRgba(state.data?.color, 0.12),
                    border: `1px solid ${c}`,
                    margin: '4px 6px 4px 0',
                    alignItems: 'center',
                };
            },
            multiValueLabel: (base) => ({
                ...base,
                padding: '4px 2px 4px 6px',
            }),
            multiValueRemove: (base) => ({
                ...base,
                borderRadius: '0 999px 999px 0',
                color: '#9ca3af',
                padding: '0 6px 0 2px',
                ':hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    color: '#64748b',
                },
            }),
        }),
        [],
    );

    return (
        !loading && userObject ?
            <Container className="container">
                <div className="row mt-2">
                    {
                        userObject && (!userObject.companyID || userObject.groupID) &&
                        <div className="col-md-12 mt-2" style={{ width: '100%' }}>
                            <Select
                                options={companies?.map((c) => ({ value: c.companyID, label: c.name })) ?? []}
                                value={companies?.find((c) => c.companyID === selectedCompany) ? { value: selectedCompany, label: companies.find((c) => c.companyID === selectedCompany).name } : null}
                                onChange={(x) => onChangeCompany(x?.value ?? '')}
                                placeholder="Select a company"
                                isSearchable
                                isClearable={false}
                                styles={{ ...formSelectStyles, control: formSelectControlFullWidth }}
                                classNamePrefix="discussions-company"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    }
                    <div className="col-md-12 mt-2" style={{ width: '100%' }}>
                        <Select
                            options={cropYears?.map((y) => ({ value: y.reportYearID, label: y.year })) ?? []}
                            value={cropYears?.find((y) => y.reportYearID === selectedReportYear) ? { value: selectedReportYear, label: cropYears.find((y) => y.reportYearID === selectedReportYear).year } : null}
                            onChange={(x) => onChangeReportYearID(x?.value ?? '')}
                            placeholder="Select report year"
                            isSearchable
                            isClearable={false}
                            styles={{ ...formSelectStyles, control: formSelectControlFullWidth }}
                            classNamePrefix="discussions-reportyear"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    {!(userObject.roleID === "E759AB01-E2C8-4D38-BA5F-BA30949FB70F" && userObject.companyID == null) &&
                        <div className="col-md-12 mt-2" style={{ width: '100%' }}>
                            <Select
                                options={operations?.map((o) => ({ value: o.operationID, label: o.description })) ?? []}
                                value={
                                    selectedOperation != null && selectedOperation !== '' && operations?.find((o) => o.operationID == selectedOperation)
                                        ? { value: selectedOperation, label: operations.find((o) => o.operationID == selectedOperation).description }
                                        : null
                                }
                                onChange={(x) => onChangeOperation(x?.value ?? '')}
                                placeholder="All Forms"
                                isSearchable
                                isClearable={false}
                                styles={{ ...formSelectStyles, control: formSelectControlFullWidth }}
                                classNamePrefix="discussions-operation"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    }
                    <div className="col-md-12 mt-2" style={{ width: '100%' }}>
                        <Select
                            options={modulesFiltered?.map((m) => ({ value: m.moduleID, label: m.description })) ?? []}
                            value={modulesFiltered?.find((m) => m.moduleID == selectedModule) ? { value: selectedModule, label: modulesFiltered.find((m) => m.moduleID == selectedModule).description } : null}
                            onChange={(x) => setSelectedModule(x?.value ?? '')}
                            placeholder="All Modules"
                            isSearchable
                            isClearable={false}
                            styles={{ ...formSelectStyles, control: formSelectControlFullWidth }}
                            classNamePrefix="discussions-module"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    {
                        hasCultureTypeFilterModules &&
                            <div className="col-md-12 mt-2" style={{ width: '100%' }}>
                                <Select
                                    options={cultureTypes?.map((ct) => ({ value: ct.cultureTypeID, label: ct.description })) ?? []}
                                    // Agora aponta para tempCultureType para refletir a escolha imediata no combo
                                    value={cultureTypes?.find((ct) => ct.cultureTypeID === tempCultureType) 
                                        ? { 
                                            value: tempCultureType, 
                                            label: cultureTypes.find((ct) => ct.cultureTypeID === tempCultureType).description 
                                        } 
                                        : null
                                    }
                                    onChange={(x) => setTempCultureType(x?.value ?? null)}
                                    placeholder="All culture types"
                                    isSearchable
                                    isClearable={true}
                                    styles={{ ...formSelectStyles, control: formSelectControlFullWidth }}
                                    classNamePrefix="discussions-culturetype"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                    }
                    <div className="col-md-12 mt-2" style={{ width: '100%' }}>
                        <Select
                            inputId="filter-tag-select"
                            isMulti
                            isClearable
                            isSearchable
                            placeholder="All Tags"
                            closeMenuOnSelect={false}
                            blurInputOnSelect={false}
                            options={tags}
                            value={tags.filter((opt) => selectedTag?.includes(opt.value))}
                            onChange={(selectedOptions) => {
                                setSelectedTag(selectedOptions ? selectedOptions.map(o => o.value) : []);
                            }}
                            components={discussionsTagFilterSelectComponents}
                            styles={discussionsTagSelectStyles}
                            classNamePrefix="discussions-tag"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            formatOptionLabel={(opt) => {
                                const c = tagPillBaseColor(opt.color);
                                return (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span
                                            aria-hidden
                                            style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: c,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <span>{opt.label}</span>
                                    </span>
                                );
                            }}
                        />
                    </div>
                    <div className="col-md-12 mt-2" style={{ width: '100%' }}>
                        <Select
                            options={[
                                { value: '', label: 'Any discussion' },
                                { value: 'Open', label: 'Ongoing discussions' },
                                { value: 'Closed', label: 'Closed discussions' },
                                { value: 'NotAnswered', label: 'Ongoing discussions not answered' },
                            ]}
                            value={[null, '', 'Open', 'Closed', 'NotAnswered'].includes(discussionStatus) ? { value: discussionStatus, label: { '': 'Any discussion', Open: 'Ongoing discussions', Closed: 'Closed discussions', NotAnswered: 'Ongoing discussions not answered' }[discussionStatus] ?? discussionStatus } : null}
                            onChange={(x) => setDiscussionStatus(x?.value ?? '')}
                            placeholder="Any discussion"
                            isSearchable={false}
                            isClearable={false}
                            styles={{ ...formSelectStyles, control: formSelectControlFullWidth }}
                            classNamePrefix="discussions-status"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    <button className="col-md-12 apply-btn mt-2" onClick={() => loadDiscussionsDashboard()}>Apply</button>
                </div>
                <div className="row">
                    {
                        loadingView ?
                            <div className="mt-2" style={{ padding: 0 }}>
                                <TableLoading />
                            </div>
                            :
                            viewData != null &&
                            <>
                                <Table id="versionTable" className="mt-5">
                                    <thead className="sticky-top">
                                        <tr>
                                            <th width="37%">KPI</th>
                                            <th width="3%" className="text-left">Value</th>
                                            <th width="15%">Section Name</th>
                                            <th width="12%">SubSection Name</th>
                                            <th width="11%">Title of Column</th>
                                            <th width="10%">Last Message Date</th>
                                            <th width="12%" className="text-left">Tags</th>
                                            <th width="3%" className="text-center"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            viewData && viewData.length > 0 ?
                                                viewData.map((item, index) => {

                                                    const unitMeasureType = item.unitMeasure?.type ?? item.unitMeasureType;
                                                    var unitMeasure = GetTypeInput(item.unitMeasureID, unitMeasureType);
                                                    const discussionId = item.discussionID ?? item.DiscussionID;
                                                    const kpiRow = kpiDiscussions?.find((d) => (d.discussionID ?? d.DiscussionID) == discussionId);
                                                    const cultureTypeFromDiscussion = kpiRow?.cultureTypeID ?? kpiRow?.CultureTypeID;
                                                    const badgeCultureName =
                                                        selectedCultureType != null
                                                            ? (cultureTypeLabelById(selectedCultureType) ?? item.cultureTypeName ?? item.CultureTypeName)
                                                            : (cultureTypeLabelById(cultureTypeFromDiscussion) ?? item.cultureTypeName ?? item.CultureTypeName);

                                                    let newValue = []
                                                    if (item.unitMeasure.inputType == "multiselectcustom" && item.value) {
                                                        try {
                                                            let newValueList = JSON.parse(item.value)
                                                            if (Array.isArray(newValueList)) {
                                                                newValue = newValueList.map(x => x.label)
                                                            }
                                                        } catch {
                                                            newValue = []
                                                        }
                                                    }

                                                    return <>
                                                        <tr key={`data_subSectionKPI_${item.kpiRefNumber}_${index}`}>
                                                            <td>
                                                                {
                                                                    badgeCultureName ?
                                                                        <span style={{ marginRight: "2px" }} className="badge">{badgeCultureName}</span>
                                                                        :
                                                                        <></>
                                                                }
                                                                {
                                                                    item.countryName ?
                                                                        <span style={{ marginRight: "2px" }} className="badge">{item.countryName}</span>
                                                                        :
                                                                        <></>
                                                                }
                                                                {
                                                                    item.fuelTypeName ?
                                                                        <span style={{ marginRight: "2px" }} className="badge">{item.fuelTypeName}</span>
                                                                        : <></>
                                                                }
                                                                {
                                                                    item.curingBarnName ?
                                                                        <span style={{ marginRight: "2px" }} className="badge">{item.curingBarnName}</span>
                                                                        : <></>
                                                                }
                                                                {
                                                                    item.fertilizerName ?
                                                                        <span style={{ marginRight: "2px" }} className="badge">{item.fertilizerName}</span>
                                                                        : <></>
                                                                }
                                                                <span>({item.kpiRefNumber}) {item.kpiDescription}</span>
                                                            </td>

                                                            <td className="discussion-value-cell text-center">
                                                                {
                                                                    item.unitMeasure.inputType == "Text" || unitMeasureType == "selectfuel" || unitMeasureType == "selectCuringBarn" || unitMeasureType == "selectcountry" || unitMeasureType == "selecttobacco" || item.unitMeasure.inputType == "selectcustom" || item.unitMeasure.inputType == "selectyn" ?
                                                                        <span>
                                                                            {
                                                                                item.value
                                                                            }
                                                                        </span>
                                                                        : (item.unitMeasure.inputType == "multiselectcustom" ?
                                                                            <span>
                                                                                {newValue.map((value, valueIndex) => (
                                                                                    <React.Fragment key={`${value}_${valueIndex}`}>
                                                                                        {value}
                                                                                        {valueIndex < newValue.length - 1 && <> ;<br /></>}
                                                                                    </React.Fragment>
                                                                                ))}
                                                                            </span>
                                                                            :
                                                                            <NumericFormat
                                                                            thousandsGroupStyle="thousand"
                                                                            value={item.value}
                                                                            decimalSeparator={unitMeasure.decimalSeparator != "" ? unitMeasure.decimalSeparator : "."}
                                                                            decimalScale={unitMeasure.decimalPrecision}
                                                                            suffix={item.value != "" ? unitMeasure.suffix : ""}
                                                                            displayType="input"
                                                                            type="text"
                                                                            disabled
                                                                            className={`read-only`}
                                                                            thousandSeparator={true}
                                                                            allowNegative={true}
                                                                            allowLeadingZeros={false}
                                                                            allowEmptyFormatting={true}
                                                                        />
                                                                        )
                                                                }
                                                            </td>
                                                            <td className="text-left">
                                                                <span>
                                                                    {item.sectionName}
                                                                </span>
                                                            </td>
                                                            <td className="text-left">
                                                                <span>
                                                                    {item.subSectionName}
                                                                </span>
                                                            </td>
                                                            <td className="text-left">
                                                                <span>
                                                                    {item.titleOfColumn}
                                                                </span>
                                                            </td>
                                                            <td className="text-left">
                                                                <span>
                                                                    {item.lastMessageDate != null || item.lastMessageDate != "" ? item.lastMessageDate : ""}
                                                                </span>
                                                            </td>
                                                            <td className="text-center discussion-tags-cell">
                                                                {(() => {
                                                                    const fromRow = item.discussionTags ?? item.DiscussionTags;
                                                                    const fromKpi = kpiRow?.discussionTags ?? kpiRow?.DiscussionTags;
                                                                    const tagList = Array.isArray(fromRow) && fromRow.length ? fromRow : (Array.isArray(fromKpi) ? fromKpi : []);
                                                                    const canSeeAllTags = getCanManageAllDiscussionTags(rolesList);
                                                                    const visibleTagList = canSeeAllTags
                                                                        ? tagList
                                                                        : tagList.filter((tg) => {
                                                                            const id = tg.tagID ?? tg.TagID;
                                                                            return id != null && allowedDiscussionTagIdSet.has(id);
                                                                        });
                                                                    if (!visibleTagList.length) {
                                                                        return <span style={{ color: '#9ca3af' }}>—</span>;
                                                                    }
                                                                    return (
                                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'baseline', gap: 6, maxWidth: 220, margin: '0 auto' }}>
                                                                            {visibleTagList.map((tg, ti) => {
                                                                                const tid = tg.tagID ?? tg.TagID ?? ti;
                                                                                const tname = tg.tagDescription ?? tg.TagDescription ?? tg.tagName ?? tg.TagName ?? '';
                                                                                const tcolor = tg.tagColor ?? tg.TagColor ?? '#9ca3af';
                                                                                return (
                                                                                    <span
                                                                                        key={`${discussionId}-${tid}-${ti}`}
                                                                                        title={tname}
                                                                                        style={{
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            gap: 4,
                                                                                            fontSize: 11,
                                                                                            lineHeight: 1.2,
                                                                                            padding: '2px 8px',
                                                                                            borderRadius: 999,
                                                                                            backgroundColor: hexToRgba(tcolor, 0.14),
                                                                                            border: `1px solid ${tcolor}`,
                                                                                            maxWidth: 200,
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap',
                                                                                        }}
                                                                                    >
                                                                                        <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: tcolor, flexShrink: 0 }} />
                                                                                        {tname}
                                                                                    </span>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td>
                                                                {(() => {
                                                                    // Buscar tipos do último Message se disponível, caso contrário usar os tipos do item
                                                                    var lastMessage = item.messages && item.messages.length > 0 
                                                                        ? item.messages[item.messages.length - 1] 
                                                                        : null;
                                                                    var resolvedFromBadge = resolveCultureTypeIdByDescription(item.cultureTypeName ?? item.CultureTypeName);
                                                                    var cultureTypeID = discussionChatCultureTypeID !== undefined
                                                                        ? discussionChatCultureTypeID
                                                                        : (cultureTypeFromDiscussion != null && cultureTypeFromDiscussion !== ''
                                                                            ? Number(cultureTypeFromDiscussion)
                                                                            : (resolvedFromBadge !== undefined
                                                                                ? resolvedFromBadge
                                                                                : (lastMessage?.cultureTypeID ?? item.cultureTypeID)));
                                                                    var fuelTypeId = lastMessage?.fuelTypeID ?? item.fuelTypeID;
                                                                    var curingBarnID = lastMessage?.curingBarnID ?? item.curingBarnID;
                                                                    var countryId = lastMessage?.countryID ?? item.countryID;
                                                                    
                                                                    return (
                                                                        <ChatWrapper>
                                                                            <ChatButton
                                                                                buttonText=""
                                                                                kpiDiscussions={kpiDiscussions}
                                                                                setChatRequest={setChatRequest}
                                                                                setShowChat={setShowChat}
                                                                                request={{
                                                                                    ...chatRequest,
                                                                                    "subSectionKPIID": item.subSectionKPIID,
                                                                                    "moduleId": item.moduleID,
                                                                                    "kpiId": item.kpiid,
                                                                                    "cultureTypeID": cultureTypeID,
                                                                                    "fuelTypeId": fuelTypeId,
                                                                                    "curingBarnID": curingBarnID,
                                                                                    "countryId": countryId,
                                                                                    "row": item.row,
                                                                                    "column": item.column,
                                                                                    "status": item.status
                                                                                }}
                                                                            />
                                                                        </ChatWrapper>
                                                                    );
                                                                })()}
                                                            </td>
                                                        </tr>
                                                    </>
                                                })
                                                :
                                                <tr><td colSpan="100%">There are no discussions for this filters.</td></tr>
                                        }
                                    </tbody>
                                </Table>
                                {/* {
                                    (userRoles.includes("RoleReviewer") || userRoles.includes("RoleDeploymentTrackerReviewer") || userRoles.includes("RoleApprover")) && userObject.companyID === null ?
                                        <button className="col-md-2 apply-btn mt-2" onClick={() => handleSendDiscussionNotification()}>Send Notifications</button>
                                        :
                                        <></>
                                } */}
                            </>
                    }
                </div>
                <ChatWindow
                    request={chatRequest}
                    show={showChat}
                    setShowChat={setShowChat}
                    loadDiscussions={loadDiscussionsDashboard}
                />
            </Container>
            :
            <Container className="container mt-4">
                <TableLoading />
            </Container>
    );
}

export default DiscussionDashboard;

